"use client"

import { useState, useEffect, useRef } from "react"
import { Lock } from "lucide-react"
import { usePatreonMode } from "@/lib/use-patreon-mode"
import { useAuthStore } from "@/lib/auth-store"
import { entitledTierLabel } from "@/lib/entitlements"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Slider } from "@/components/ui/slider"
import {
  formatPrice,
  MAX_SCRIPT_PRICE_CENTS,
  calculateCreatorRevenue,
  type CommunityScript,
} from "@/lib/marketplace-store"
import { Loader2, Upload, Image as ImageIcon, Crop, Trash2, X, RefreshCw, AlertTriangle, ExternalLink, Video, Tag, DollarSign, Hourglass, Download, PenTool, Pencil, Megaphone } from "@/lib/icons"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ImageCropper } from "@/components/image-cropper"
import { getImageUrl } from "@/lib/image-utils"
import { Badge } from "@/components/ui/badge"
import { toast } from "sonner"
import { useScriptStore } from "@/lib/script-store"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { useTranslations } from "next-intl"

export function getEditorRoute(script: CommunityScript): string {
  const rawEditorType =
    (script as any).editor_metadata?.editorType ??
    (script as any).editorMetadata?.editorType ??
    script.script_content?.metadata?.editorMetadata?.editorType ??
    script.script_content?.metadata?.editorType

  const rawEditorMode =
    (script as any).editor_metadata?.editorMode ??
    (script as any).editorMetadata?.editorMode ??
    script.script_content?.metadata?.editorMetadata?.editorMode ??
    script.script_content?.metadata?.editorMode

  if (rawEditorType === "story-scripter") return "story-scripter"
  if (rawEditorType === "vertical-editor" || rawEditorMode === "vertical") return "vertical-editor"
  if (rawEditorType === "block-editor" || rawEditorMode === "block") return "block-editor"
  if (rawEditorType === "manual-editor" || rawEditorType === "manual-control" || rawEditorMode === "manual") return "manual-control"
  return "curve-editor"
}

interface EditScriptDialogProps {
  script: CommunityScript | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdated: () => void
}

export function EditScriptDialog({ script, open, onOpenChange, onUpdated }: EditScriptDialogProps) {
  const profile = useAuthStore((s) => s.profile)
  const tt = useTranslations("editScriptDialog")
  const tLocked = useTranslations("processorLocked")
  // Patreon mode: prices cannot be changed while there is no payment processor. The script keeps its current price (saving never changes it).
  const { enabled: patreonMode } = usePatreonMode()
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [isFree, setIsFree] = useState(true)
  const [priceInput, setPriceInput] = useState("")
  const [downloadPriceInput, setDownloadPriceInput] = useState("")
  const [trialDuration, setTrialDuration] = useState(30)
  const [allowDownload, setAllowDownload] = useState(true)
  const [allowPurchaserEdit, setAllowPurchaserEdit] = useState(false)
  const [previewVideoUrl, setPreviewVideoUrl] = useState("")
  const [thumbnailUrl, setThumbnailUrl] = useState("")
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [status, setStatus] = useState<"active" | "paused">("active")
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState("")

  // New script version state
  const [newScriptFile, setNewScriptFile] = useState<File | null>(null)
  const [newScriptActions, setNewScriptActions] = useState<any | null>(null)
  const [newScriptSourceName, setNewScriptSourceName] = useState<string | null>(null)
  const [updateNote, setUpdateNote] = useState("")
  const [scriptVersion, setScriptVersion] = useState<number>(1)

  const router = useRouter()
  const localScripts = useScriptStore((s) => s.scripts)
  const addScript = useScriptStore((s) => s.addScript)

  // Cropper state
  const [cropperOpen, setCropperOpen] = useState(false)
  const [cropperImageSrc, setCropperImageSrc] = useState("")
  const [isPickingFrame, setIsPickingFrame] = useState(false)
  const pickFrameVideoRef = useRef<HTMLVideoElement | null>(null)

  // Populate form when script changes
  useEffect(() => {
    if (!script) return
    setTitle(script.title)
    setDescription(script.description || "")
    setThumbnailUrl(script.thumbnail_url || "")
    setPreviewVideoUrl(script.preview_video_url || "")
    setTrialDuration(script.trial_duration_seconds ?? 30)
    setAllowDownload(script.allow_download !== false)
    setAllowPurchaserEdit(script.allow_purchaser_edit === true)
    setDownloadPriceInput(
      (script as any).download_price_cents
        ? ((script as any).download_price_cents / 100).toFixed(2)
        : ""
    )
    setStatus(script.status === "active" ? "active" : "paused")
    setTags(script.tags || [])
    setNewScriptFile(null)
    setNewScriptActions(null)
    setNewScriptSourceName(null)
    setUpdateNote("")
    setScriptVersion((script as any).script_content_version ?? 1)

    if (script.price_cents === 0) {
      setIsFree(true)
      setPriceInput("")
    } else {
      setIsFree(false)
      setPriceInput((script.price_cents / 100).toFixed(2))
    }
  }, [script])

  const getPriceCents = (): number => {
    if (isFree) return 0
    const parsed = parseFloat(priceInput)
    if (isNaN(parsed) || parsed < 0) return 0
    return Math.round(parsed * 100)
  }

  const getDownloadPriceCents = (): number | null => {
    if (!downloadPriceInput.trim()) return null
    const parsed = parseFloat(downloadPriceInput)
    if (isNaN(parsed) || parsed < 0) return null
    return Math.round(parsed * 100)
  }

  const priceError = (() => {
    if (isFree) return null
    const cents = getPriceCents()
    if (!priceInput.trim()) return null
    if (cents > 0 && cents < 99) return "Minimum price is $0.99"
    if (cents > MAX_SCRIPT_PRICE_CENTS) return `Maximum price is ${formatPrice(MAX_SCRIPT_PRICE_CENTS)}`
    return null
  })()

  const downloadPriceError = (() => {
    const dlCents = getDownloadPriceCents()
    if (dlCents === null) return null
    if (dlCents < 99) return "Minimum download price is $0.99"
    if (dlCents > MAX_SCRIPT_PRICE_CENTS) return `Maximum is ${formatPrice(MAX_SCRIPT_PRICE_CENTS)}`
    if (dlCents < getPriceCents() + 50) return "Download price must be at least $0.50 higher than the play price"
    return null
  })()

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase().replace(/\s+/g, "-")
    if (tag && !tags.includes(tag) && tags.length < 15) {
      setTags([...tags, tag])
      setTagInput("")
    }
  }

  const handleScriptFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setNewScriptFile(file)
    setNewScriptSourceName(null)
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string)
        setNewScriptActions(parsed)
      } catch {
        toast.error(tt("invalidFunscriptFile"))
        setNewScriptFile(null)
        setNewScriptSourceName(null)
      }
    }
    reader.readAsText(file)
    e.target.value = ""
  }

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (event) => {
      setCropperImageSrc(event.target?.result as string)
      setCropperOpen(true)
    }
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  const handleCropExistingThumbnail = () => {
    if (thumbnailUrl) {
      setCropperImageSrc(thumbnailUrl)
      setCropperOpen(true)
    }
  }

  const handlePickFromVideo = async () => {
    const videoUrl = script?.linked_content_url
    if (!videoUrl) return
    setIsPickingFrame(true)
    try {
      const video = document.createElement("video")
      pickFrameVideoRef.current = video
      video.src = getImageUrl(videoUrl)
      video.crossOrigin = "anonymous"
      video.muted = true
      await new Promise<void>((resolve, reject) => {
        video.addEventListener("loadeddata", () => resolve(), { once: true })
        video.addEventListener("error", () => reject(new Error("Video load failed")), { once: true })
      })
      video.currentTime = Math.min(1, video.duration * 0.1)
      await new Promise<void>((res) => {
        video.addEventListener("seeked", () => res(), { once: true })
      })
      const canvas = document.createElement("canvas")
      canvas.width = video.videoWidth || 1280
      canvas.height = video.videoHeight || 720
      const ctx = canvas.getContext("2d")
      if (!ctx) return
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => {
        if (blob) {
          setCropperImageSrc(URL.createObjectURL(blob))
          setCropperOpen(true)
        }
        video.src = ""
        pickFrameVideoRef.current = null
      }, "image/jpeg", 0.9)
    } catch {
      toast.error(tt("couldNotLoadVideoTryUploadingACustomIm"))
    } finally {
      setIsPickingFrame(false)
    }
  }

  const handleCropComplete = (url: string) => {
    setThumbnailUrl(url)
  }

  const handleSave = async () => {
    if (!script) return

    const priceCents = getPriceCents()
    if (!isFree && (priceCents < 99 || priceCents > MAX_SCRIPT_PRICE_CENTS)) {
      setError(tt("priceMustBeFreeOrBetween099And4999"))
      return
    }

    const downloadPriceCents = getDownloadPriceCents()
    if (downloadPriceError) {
      setError(downloadPriceError)
      return
    }

    setIsSaving(true)
    setError("")

    try {
      const body: any = {
        title,
        description,
        tags,
        thumbnail_url: thumbnailUrl || undefined,
        preview_video_url: previewVideoUrl.trim() || undefined,
        price_cents: priceCents,
        download_price_cents: downloadPriceCents,
        trial_duration_seconds: trialDuration,
        allow_download: downloadPriceInput.trim() ? true : allowDownload,
        allow_purchaser_edit: allowPurchaserEdit && (!!downloadPriceInput.trim() || allowDownload),
        status,
      }
      if (newScriptActions) {
        body.script_content = newScriptActions
        body.update_note = updateNote.trim() || null
      }

      const res = await fetch(`/api/marketplace/scripts/${script.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || "Failed to update")
      }

      toast.success(newScriptActions ? tt("scriptUpdatedBuyersWillSeeANewVersionA") : tt("scriptUpdatedSuccessfully"))
      onUpdated()
      onOpenChange(false)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  if (!script) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="pl-6 pr-14 pt-6 pb-4 shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <DialogTitle className="text-base truncate">{script.title}</DialogTitle>
                <DialogDescription className="text-xs mt-0.5">
                  {tt("manageYourPublishedScript")}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant={status === "active" ? "default" : "secondary"} className="text-xs">
                  {status === "active" ? "Live" : "Paused"}
                </Badge>
                <Switch
                  checked={status === "active"}
                  onCheckedChange={(checked) => setStatus(checked ? "active" : "paused")}
                  className="scale-90"
                />
              </div>
            </div>
          </DialogHeader>

          <Tabs defaultValue="details" className="flex flex-col flex-1 min-h-0">
            <TabsList className="mx-6 shrink-0 h-8 mb-0 rounded-none border-b border-border bg-transparent justify-start gap-0 p-0">
              {(["details", "pricing", "content", "promote"] as const).map((t) => (
                <TabsTrigger
                  key={t}
                  value={t}
                  className="h-8 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent capitalize text-xs px-4"
                >
                  {t}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* ── Details ── */}
            <TabsContent value="details" className="flex-1 overflow-y-auto px-6 py-4 space-y-4 mt-0">
              {/* Cover Image */}
              <div className="space-y-2">
                <Label>{tt("coverImage")}</Label>
                <div className="flex items-start gap-3">
                  <div className="w-24 h-16 rounded-lg border border-border overflow-hidden bg-muted shrink-0 flex items-center justify-center">
                    {thumbnailUrl
                      ? <img src={thumbnailUrl} alt={tt("cover")} className="w-full h-full object-cover" />
                      : <ImageIcon className="h-6 w-6 text-muted-foreground/40" />}
                  </div>
                  <div className="flex flex-col gap-1.5 w-36">
                    {thumbnailUrl && (
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs w-full justify-start" onClick={handleCropExistingThumbnail}>
                        <Crop className="h-3 w-3 mr-1.5" />{tt("cropAdjust")}
                      </Button>
                    )}
                    {script.content_type === "video" && script.linked_content_url && (
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs w-full justify-start" onClick={handlePickFromVideo} disabled={isPickingFrame}>
                        {isPickingFrame
                          ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                          : <Video className="h-3 w-3 mr-1.5" />}
                        {isPickingFrame ? tt("loading") : tt("useVideoFrame")}
                      </Button>
                    )}
                    <label className="cursor-pointer block w-full">
                      <input type="file" accept="image/*" className="hidden" onChange={handleThumbnailUpload} />
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs w-full justify-start" asChild>
                        <span><Upload className="h-3 w-3 mr-1.5" />{tt("uploadNew")}</span>
                      </Button>
                    </label>
                    {thumbnailUrl && (
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs w-full justify-start text-destructive" onClick={() => setThumbnailUrl("")}>
                        <Trash2 className="h-3 w-3 mr-1.5" />{tt("remove")}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{tt("title")}</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tt("scriptTitle")} />
              </div>

              <div className="space-y-2">
                <Label>{tt("description")}</Label>
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={tt("describeYourScript")} rows={3} />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />{tt("tags")}</Label>
                <div className="flex gap-2">
                  <input
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag() } }}
                    placeholder={tt("addATagPressEnter")}
                  />
                  <button type="button" onClick={addTag} className="px-3 rounded-md border border-input text-sm hover:bg-accent transition-colors">{tt("add")}</button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                        {tag}
                        <button type="button" title={`Remove ${tag}`} onClick={() => setTags(tags.filter((t) => t !== tag))} className="hover:text-destructive transition-colors">
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>{tt("previewVideoURL")} <span className="text-muted-foreground text-xs">{tt("optional")}</span></Label>
                <Input value={previewVideoUrl} onChange={(e) => setPreviewVideoUrl(e.target.value)} placeholder={tt("https")} />
              </div>
            </TabsContent>

            {/* ── Pricing ── */}
            <TabsContent value="pricing" className="flex-1 overflow-y-auto px-6 py-4 space-y-4 mt-0">
              <div className="space-y-2">
                <Label>{tt("price")}</Label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={isFree} disabled={patreonMode} onCheckedChange={(checked) => { setIsFree(checked); if (checked) setPriceInput("") }} />
                    <span className="text-sm text-muted-foreground">{tt("free")}</span>
                    {patreonMode && <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
                  </div>
                  {!isFree && (
                    <div className="relative flex-1">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input type="number" min="0.99" max="49.99" step="0.01" value={priceInput} disabled={patreonMode} onChange={(e) => setPriceInput(e.target.value)} placeholder="4.99" className="pl-7 w-32" />
                    </div>
                  )}
                </div>
                {patreonMode && <p className="text-xs text-muted-foreground">{tLocked("pricingLocked")}</p>}
                {priceError && <p className="text-xs text-destructive">{priceError}</p>}
                {!isFree && getPriceCents() >= 99 && (
                  <p className="text-xs text-green-500 flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    {tt("youEarnValue", { value: formatPrice(calculateCreatorRevenue(getPriceCents(), entitledTierLabel(profile as any))) })}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{tt("trialDuration")}</Label>
                  <span className="text-sm flex items-center gap-1 text-muted-foreground">
                    <Hourglass className="h-3 w-3" />{tt("trialDurationS", { trialDuration })}
                  </span>
                </div>
                <Slider value={[trialDuration]} onValueChange={([v]) => setTrialDuration(v)} min={10} max={120} step={5} />
              </div>

              {/* Download tier price */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  {tt("downloadPrice")} <span className="text-muted-foreground text-xs font-normal">{tt("optionalUpsell")}</span>
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    type="number"
                    min={getPriceCents() > 0 ? ((getPriceCents() + 50) / 100).toFixed(2) : "0.99"}
                    max="49.99"
                    step="0.01"
                    value={downloadPriceInput}
                    disabled={patreonMode}
                    onChange={(e) => setDownloadPriceInput(e.target.value)}
                    placeholder={getPriceCents() > 0 ? ((getPriceCents() + 50) / 100).toFixed(2) : "0.99"}
                    className="pl-7 w-32"
                  />
                </div>
                {downloadPriceError
                  ? <p className="text-xs text-destructive">{downloadPriceError}</p>
                  : downloadPriceInput.trim()
                  ? <p className="text-xs text-muted-foreground">{tt("play")} <strong>${(getPriceCents() / 100 || 0).toFixed(2)}</strong> {tt("download")} <strong>${parseFloat(downloadPriceInput || "0").toFixed(2)}</strong></p>
                  : <p className="text-xs text-muted-foreground">{tt("leaveBlankToUseTheAllowDownloadToggleBel")}</p>
                }
              </div>

              {/* Allow Download (simple toggle — only when no download tier price set) */}
              {!downloadPriceInput.trim() && (
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label className="flex items-center gap-2"><Download className="h-4 w-4" />{tt("allowDownload")}</Label>
                    <p className="text-xs text-muted-foreground">{tt("letAnyBuyerDownloadTheRawFunscriptFileF")}</p>
                  </div>
                  <Switch checked={allowDownload} onCheckedChange={setAllowDownload} />
                </div>
              )}

              {/* Allow Editing — only relevant for free-download; download tier always includes edit */}
              {downloadPriceInput.trim() ? (
                <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground flex items-center gap-2">
                  <PenTool className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                  {tt("downloadTierAutomaticallyIncludesInAppEdit")}
                </div>
              ) : (
                <div className={`flex items-center justify-between rounded-lg border p-3 ${!allowDownload ? "opacity-40 pointer-events-none" : ""}`}>
                  <div className="space-y-0.5">
                    <Label className="flex items-center gap-2"><PenTool className="h-4 w-4" />{tt("allowEditing")}</Label>
                    <p className="text-xs text-muted-foreground">{tt("letBuyersOpenThisScriptInTheirEditorToC")}</p>
                  </div>
                  <Switch
                    checked={allowPurchaserEdit}
                    onCheckedChange={setAllowPurchaserEdit}
                    disabled={!allowDownload}
                  />
                </div>
              )}
            </TabsContent>

            {/* ── Content / Versions ── */}
            <TabsContent value="content" className="flex-1 overflow-y-auto px-6 py-4 space-y-3 mt-0">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" />{tt("scriptContent")}</Label>
                <Badge variant="outline" className="text-[10px]">{tt("vScriptVersion", { scriptVersion })}</Badge>
              </div>
              <p className="text-xs text-muted-foreground leading-snug">
                {tt("pushANewVersionExistingBuyersKeepTheirO")}
              </p>

              {script.script_content?.actions?.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const targetEditor = getEditorRoute(script)
                    const newId = addScript({
                      name: `${script.title} (edit)`,
                      description: script.description ?? "",
                      funscript: {
                        version: script.script_content.version ?? "1.0",
                        inverted: script.script_content.inverted ?? false,
                        range: script.script_content.range ?? 100,
                        actions: script.script_content.actions,
                        metadata: {
                          ...(script.script_content.metadata ?? {}),
                          title: script.title,
                        },
                      },
                      isPublished: false,
                      tags: script.tags ?? [],
                      deviceCompatibility: [],
                      averageIntensity: 50,
                      contentUrl: script.linked_content_url ?? undefined,
                      contentSite: script.linked_content_site ?? undefined,
                      contentType: (script.content_type as any) ?? undefined,
                      thumbnailUrl: script.thumbnail_url ?? undefined,
                      contentId: script.linked_content_id ?? undefined,
                      pricing: {
                        isPurchasable: true,
                        price: getPriceCents(),
                        allowDownload: downloadPriceInput.trim() ? true : allowDownload,
                        trialDurationSeconds: trialDuration,
                      },
                      marketplaceDraft: {
                        sourceScriptId: script.id,
                        sourceVersion: (script as any).script_content_version ?? 1,
                        visibility: getPriceCents() > 0 ? "paid" : "free",
                        priceCents: getPriceCents(),
                        downloadPriceCents: getDownloadPriceCents(),
                        trialDuration,
                        allowDownload: downloadPriceInput.trim() ? true : allowDownload,
                        allowPurchaserEdit,
                      } as any,
                      editorMetadata: {
                        editorType: targetEditor === "manual-control" ? "manual-editor" : targetEditor as any,
                        lastEditorState:
                          script.script_content?.metadata?.editorMetadata?.lastEditorState ??
                          script.script_content?.metadata?.lastEditorState,
                      },
                    } as any)
                    onOpenChange(false)
                    router.push(`/?tab=scripter&editor=${targetEditor}&scriptId=${encodeURIComponent(newId)}`)
                    toast.info(tt("titleEditLoadedMakeYourChangesThe", { title: script.title }))
                  }}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-primary/40 text-primary hover:bg-primary/10 transition-colors w-full justify-center"
                >
                  <Pencil className="h-3 w-3" />
                  {tt("openCurrentVersionInEditor")}
                </button>
              )}

              {newScriptActions ? (
                <div className="flex items-center gap-2 text-xs">
                  <div className="flex-1 px-2 py-1.5 rounded bg-green-500/10 border border-green-500/20 text-green-400 flex items-center gap-1.5">
                    <RefreshCw className="h-3 w-3 shrink-0" />
                    <span className="truncate">{newScriptSourceName ?? newScriptFile?.name ?? "Script selected"}</span>
                    <span className="shrink-0">{tt("valueActions", { value: newScriptActions?.actions?.length ?? "?" })}</span>
                  </div>
                  <button type="button" title={tt("clear")} onClick={() => { setNewScriptFile(null); setNewScriptActions(null); setNewScriptSourceName(null); setUpdateNote("") }} className="text-muted-foreground hover:text-destructive transition-colors">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {localScripts.length > 0 && (
                    <Select onValueChange={(id) => {
                      const s = localScripts.find((ls) => ls.id === id)
                      if (!s) return
                      setNewScriptActions(s.funscript)
                      setNewScriptSourceName(s.name)
                      setNewScriptFile(null)
                    }}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={tt("pickFromMyScripts")} /></SelectTrigger>
                      <SelectContent>
                        {localScripts.map((s) => (
                          <SelectItem key={s.id} value={s.id} className="text-xs">
                            {s.name}
                            <span className="ml-2 text-muted-foreground">{tt("valueActions2", { value: s.funscript?.actions?.length ?? 0 })}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept=".funscript,.json" className="hidden" onChange={handleScriptFileUpload} />
                    <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-dashed border-border/60 hover:border-primary/50 hover:bg-muted/30 transition-colors">
                      <Upload className="h-3 w-3" />
                      {localScripts.length > 0 ? tt("orUploadAFunscriptFile") : tt("uploadFunscriptFile")}
                    </span>
                  </label>
                </div>
              )}

              {newScriptActions && (
                <div className="space-y-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{tt("patchNotesForBuyers")}</Label>
                    <Textarea
                      value={updateNote}
                      onChange={(e) => setUpdateNote(e.target.value)}
                      rows={3}
                      placeholder={tt("whatChangedInThisVersion")}
                      className="text-xs"
                    />
                  </div>
                  <div className="flex items-start gap-1.5 text-[11px] text-amber-400/80">
                    <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                    {tt("goesLiveOnSaveBuyersCanKeepTheirOrigina")}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ── Promote ── */}
            <TabsContent value="promote" className="flex-1 overflow-y-auto px-6 py-4 mt-0">
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-sm">{tt("promoteThisScript")}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {tt("runAdCampaignsToFeatureYourScriptInTheB")}
                </p>
                <Button size="sm" asChild>
                  <Link href="/creator/dashboard">
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    {tt("goToCreatorDashboard")}
                  </Link>
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="px-6 py-4 border-t shrink-0 flex items-center justify-between gap-3">
            {error && <p className="text-xs text-destructive flex-1">{error}</p>}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>{tt("cancel")}</Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving || !!priceError || !!downloadPriceError}>
                {isSaving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />{tt("saving")}</> : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ImageCropper
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        imageSrc={cropperImageSrc}
        aspect={16 / 9}
        onCropComplete={handleCropComplete}
      />
    </>
  )
}
